import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {BasicImage, GameLanguages, PlainDetails, PointRange} from "../../../types/admin";
import {changeGameDetail} from "../adminGameView";
import {ModalState} from "../../../types/modals";
import {toastSuccess} from "../admin";


export type GameEditModalState = ModalState & {
    languageTab: string
    game_id: string | null
    version_id: string | null
    current_version_id: string | null
    languages: GameLanguages[]
    active_languages: string[]
    display_correct_answers: boolean
    display_end_screen_points: boolean
    restrict_playing: boolean
    show_lift: boolean
    end_points: PointRange[]
    min_points: number
    max_points: number
    teams: PlainDetails[]
    collections: PlainDetails[]
    form: PlainDetails | null
    end_form: PlainDetails | null
    form_options: PlainDetails[]
    theme: PlainDetails | null
    theme_options: PlainDetails[]
    image: BasicImage | null
    isUploading: boolean
    notification_emails: string
    allow_answer_change: boolean

}

const initialState: GameEditModalState =  {
    loading: false,
    isOpen: false,
    error: null,
    languageTab: '',
    game_id: null,
    version_id: null,
    current_version_id: null,
    languages: [],
    active_languages: [],
    display_correct_answers: true,
    display_end_screen_points: true,
    restrict_playing: false,
    show_lift: true,
    end_points: [],
    min_points: 0,
    max_points: 0,
    teams: [],
    collections: [],
    form: null,
    end_form: null,
    form_options: [],
    theme: null,
    theme_options: [],
    image: null,
    isUploading: false,
    notification_emails: '',
    allow_answer_change: false
}

type GameModalPayload = {
    game_id: string | null,
    version_id: string | null,
    language: string
}

export type NewGamePayload = {
    languages: GameLanguages[]
    display_correct_answers: boolean
    display_end_screen_points: boolean
}

export type UpdateGamePayload = NewGamePayload & {
    game_id: string,
    version_id: string,
    min_points: number,
    end_points: PointRange[]
    teams: PlainDetails[]
    collections: PlainDetails[]
    form: PlainDetails | null
    end_form: PlainDetails | null
    theme: PlainDetails | null
    restrict_playing: boolean,
    show_lift: boolean,
    notification_emails: string,
    allow_answer_change: boolean
}

export const getGameDetails = createAsyncThunk(
    'gameEditModal/getGameDetails',
    async (payload: { game_id: string, version_id:string }, thunkAPI) => {
        const {game_id, version_id} = payload;
        try {
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const saveNewGame = createAsyncThunk(
    'gameEditModal/saveNewGame',
    async (payload: NewGamePayload, thunkAPI) => {
        try {
            return await api.post(`/API/admin/games/`, payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveGame = createAsyncThunk(
    'gameEditModal/saveGame',
    async (payload: UpdateGamePayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.patch(`/API/admin/games/${payload.game_id}/versions/${payload.version_id}/`, payload);
            dispatch(changeGameDetail(response));
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const uploadGameImage = createAsyncThunk(
    'gameEditModal/uploadGameImage',
    async (payload: FormData, thunkAPI) => {
        const game_id = payload.get('game_id');
        const language_code = payload.get('language_code');
        if (game_id === null || language_code === null) {
            return thunkAPI.rejectWithValue('No game id or language');
        }
        try {
            return await api.post(`/API/admin/games/${game_id}/image/`, payload, true);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const uploadGameOgImage = createAsyncThunk(
    'gameEditModal/uploadGameOgImage',
    async (payload: FormData, thunkAPI) => {
        const game_id = payload.get('game_id');
        const language_code = payload.get('language_code');
        if (game_id === null || language_code === null) {
            return thunkAPI.rejectWithValue('No game id or language');
        }
        try {
            return await api.post(`/API/admin/games/${game_id}/og-image/`, payload, true);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteGameLanguage = createAsyncThunk(
    'gameEditModal/deleteGameLanguage',
    async (payload: { game_id: string, version_id: string, language: string }, thunkAPI) => {
        const {game_id, version_id} = payload;
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.delete(`/API/admin/games/${game_id}/versions/${version_id}/languages/`, payload);
            dispatch(changeGameDetail(response));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


const slice = createSlice({
    name: 'gameEditModal',
    initialState,
    reducers: {
        openModal: (state, action: PayloadAction<GameModalPayload>) => {
            if(action.payload.game_id && action.payload.version_id) {
                state.game_id = action.payload.game_id;
                state.current_version_id = action.payload.version_id;
            }
            state.languageTab = action.payload.language;
            state.isOpen = true;
        },
        setActiveLanguage: (state, action: PayloadAction<string>) => {
            state.languageTab = action.payload;
        },
        languageAdded: (state, action) => {
            Object.assign(state, action.payload);
            state.languageTab = action.payload.language;
            state.isOpen = true;
        },
        closeGameModal: () => {
            return initialState;
        },
        changeGameModal: (state, action: PayloadAction<Partial<GameEditModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(deleteGameLanguage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteGameLanguage.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteGameLanguage.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(uploadGameImage.pending, (state) => {
                state.isUploading = true
            })
            .addCase(uploadGameImage.fulfilled, (state, action) => {
                state.isUploading = false
                if (action.payload.language_code){
                    const languageIndex = state.languages.findIndex((lang) => lang.language_code === action.payload.language_code);
                    if (languageIndex !== -1){
                        state.languages[languageIndex].image = action.payload.image;
                    }
                }
            })
            .addCase(uploadGameImage.rejected, (state, action) => {
                state.isUploading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getGameDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getGameDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                console.log(action.payload);
                Object.assign(state, action.payload);
            })
            .addCase(getGameDetails.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(uploadGameOgImage.pending, (state) => {
                state.isUploading = true
            })
            .addCase(uploadGameOgImage.fulfilled, (state, action) => {
                state.isUploading = false
                if (action.payload.language_code){
                    const languageIndex = state.languages.findIndex((lang) => lang.language_code === action.payload.language_code);
                    if (languageIndex !== -1){
                        state.languages[languageIndex].og_image = action.payload.og_image;
                    }
                }
            })
            .addCase(uploadGameOgImage.rejected, (state, action) => {
                state.isUploading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewGame.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewGame.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null
                state.current_version_id = action.payload.current_version_id;
                state.game_id = action.payload.game_id;
            })
            .addCase(saveNewGame.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveGame.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveGame.fulfilled, () => {
                return initialState;
            })
            .addCase(saveGame.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
    }
});

export const {
    openModal,
    closeGameModal,
    changeGameModal,
    setActiveLanguage,
    languageAdded
} = slice.actions;
export default slice.reducer;
